import AppContextProvider from '@/components/providers/AppContextProvider';
import { AuthContextProvider } from '@/components/providers/AuthContextProvider';
import { LoadingContextProvider } from '@/components/providers/LoadingContextProvider';
import { OrpUserProfilesProvider } from '@/components/providers/OrpUserProfilesProvider';
import { PermissionsContextProvider } from '@/components/providers/PermissionsContextProvider';
import { PageRouter } from '@/components/router/PageRouter';
import { listenForFocus } from '@/components/shared/api/focusManager';
import { SideDrawerGlobalStyles } from '@/components/shared/components/SideDrawer';
import { PageRoutes } from '@/components/shared/routes';
import { Config } from '@/components/shared/utils/config';
import OktaAuth, { toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { focusManager, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GlobalStyles, GlobalStylesProvider } from '@triumph/design-system';
import { useLocation, useNavigate } from 'react-router-dom';

const env = Config.Environment.toLowerCase();
if (env !== 'prod') {
    document.title = `[${Config.Environment.toUpperCase()}] - Originations Review Portal`;
}

const oktaAuth = new OktaAuth({
    issuer: Config.OktaIssuer,
    clientId: Config.OktaClientId,
    redirectUri: window.location.origin + '/login/callback',
});

const queryClient = new QueryClient();
focusManager.setEventListener(listenForFocus);

export const App = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
        navigate(toRelativeUrl(originalUri || '/', window.location.origin));
    };

    oktaAuth.authStateManager.subscribe(async (authState) => {
        const originalUri = window.location.href;
        if (
            !authState.isAuthenticated &&
            ![PageRoutes.AUTH_ERROR, PageRoutes.LOGIN_CALLBACK].some((page) => page === pathname)
        ) {
            await oktaAuth.signInWithRedirect({
                originalUri,
            });
        }
    });

    return (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            <AppContextProvider>
                <GlobalStylesProvider StylesComponent={<GlobalStyles />}>
                    <SideDrawerGlobalStyles />
                    <QueryClientProvider client={queryClient}>
                        <LoadingContextProvider>
                            <AuthContextProvider>
                                <PermissionsContextProvider>
                                    <OrpUserProfilesProvider>
                                        <PageRouter />
                                    </OrpUserProfilesProvider>
                                </PermissionsContextProvider>
                            </AuthContextProvider>
                        </LoadingContextProvider>
                    </QueryClientProvider>
                </GlobalStylesProvider>
            </AppContextProvider>
        </Security>
    );
};

export default App;
